/**
 * Client-side Rapid Extract view model definitions and helpers
 * @module
 */
import * as _ from 'lodash'
import * as pluralize from 'pluralize'
import { Variable, VString, VBoolean, all } from 'alkali'
import { indexedUnits, indexedRefdUnits } from './units'
import QuantifierParser, { PLUS_MINUS } from 'util/QuantifierParser'
import { lookupUnit, isNAUnit, validateQuantifier } from 'util/quantifier'
import {
	isRapidExtract,
	inferRowTypes,
	diffExtractGroups,
	diffExtractRows,
	diffExtractCell, str,  tryParseQuantifier
} from 'services/extract/util'
import {
	Extraction,
	EGroup,
	ERow,
	ETerm,
	DAStudy,
	DAStudyGroup,
	DAStudyRow,
	DAStudyOutcome,
	DAIntervention,
	ExtractionRequest,
	EIntervention,
	StudyAnnotations, EName
} from 'services/extract/types'
import {
	detectInvalidNewGroups,
	detectOverwrite,
	ExtractionComparison,
	getComparisons, studyIsAccessible
} from 'components/extract/model/comparison'
import {
	Opaque,
	Alternatives,
	SerializedAlternatives,
	SerializedPhrase,
	Phrase,
	Sense,
	splitAlternatives,
	splitPhrase,
	joinPhrase,
	QueryAnnotation,
	QueryAnnotations, joinAlternatives
} from 'util/terms'
import {
	Validation,
	empty,
	normalize,
	validateFn,
	validResult,
	validationCtx,
	validPositiveNumber, validStrictlyPositiveNumber,
	validPositiveInteger, notEmpty
} from './validation'
import { RE_FORMATTED_CI, TIMEPOINT, BINARY_VALUE, CONT_VALUE, CTNS_RE, RegExpScanner, classifyQuantifier } from './Scanner'
import { Annotation } from './Annotations'
import { phraseRelated, PLACEBO_SENSE } from './DocSearch'
import { StringGrouped, StringKeyed } from 'types/util'
import { post } from 'tools/request'
import studyUrls from 'components/studies/studyUrls'
import { createExtractItvn, createExtractRow, groupId, toExtractGroup, toExtractRow } from 'services/extract/generate'
import StudyContext from 'components/extract/model/StudyContext'

export { QueryAnnotations }

const BINARY_STANDARD = {
	fieldType: 'Binary',
	rowType: null
}

/**
 * Calculate eligible actions for the currently selected study, outcome and groups
 */
export function inferExtractState(study, groups, outcome, user) {
	const clientId = user?.Client?.Id
	if (!study)
		return {
			action: 'view',
			title: 'No study selected'
		}
	let databaseType = study.source
	let isCTG =
		databaseType == 'ClinicalTrials.gov'
	let isEU =
		databaseType == 'EU Clinical Trials'
	const urls = studyUrls(study)
	const preferred = urls.find(e => e.source == 'CTG') ||
		urls.find(e => e.source == 'PubMed') ||
		urls.find(e => e.source == 'Full Text')
	let sourceUrl = preferred?.url || '#'
	const hasResults = _.some(study.outcomes) && _.some(study.groups)
	const hasData = study.hasDocData || study.hasRapidExtract
	const hasExternalData = !hasResults && hasData
	const effectiveOutcome = all([groups, outcome], (groups: DAStudyGroup[], outcome: DAStudyOutcome) =>
		// only consider outcome selected when groups are selected
		groups?.length ? outcome : undefined
	)
	const canExtract = user?.FeatureConfig?.['Rapid Extract']?.Enabled && extractEligible(study)
	return all([groups, effectiveOutcome], (groups: DAStudyGroup[] = [], outcome: DAStudyOutcome) => {
		const comparisons = getComparisons(groups, outcome ? [outcome] : [], clientId, studyIsAccessible(study, user))
		const editing = comparisons.length && comparisons.every(c => c.editable)
		const uneditable = comparisons.some(c => c.uneditable)
		const validEdit = editing && !uneditable
		const invalidEdit = uneditable
		const action = canExtract ? (validEdit ? 'edit' : 'add') : 'view'
		const title = action == 'view' ? (isCTG || isEU ? 'View Summary' : 'View Abstract') :
			action == 'edit' ? 'Has user extracted data available; may edit values' :
				action == 'add' ?
					!hasData ? 'Has no extracted data available' :
						invalidEdit ? 'Has data extracted, but cannot edit outcome selected' :
							'Has data extracted, but not for the outcome selected'
					: ''
		return {
			study,
			user,
			action,
			title,
			groups,
			outcome,
			effectiveOutcome,
			manage: action == 'view' && isCTG, // allow managing groups and outcomes in otherwise read-only view for CTG
			annotate: action != 'view'
		}
	})
}

const isNumber = (s = '') => (s = s.trim()).length && Number.isFinite(Number(s))
export const validCountValue = validationCtx(null, validateFn(validPositiveInteger, 'Must be a positive whole number'))
export const validBinaryPctValue = validationCtx(null, validateFn(validPositiveNumber, 'Must be positive number'))
export const validBinaryCountValue = validCountValue
export const validContinuousValue = validationCtx('Continuous Value', validateFn(isNumber))

/** Continuous values are represented as quantifiers */
export const VContinuousRowValue = VString.with({
	validate: s => validContinuousQuantifier(s.valueOf())
})
/** Continuous scalar value */
export const VContinuousNum = VString.with({
	validate: s => validContinuousValue(s.valueOf())
})
/** Continuous SD/SE value */
export const VSD = VString.with({
	validate: s => validSDValue(s.valueOf())
})

export const emptyUnit = u => !u || !u.trim() || isNAUnit(u) //u == QuantifierParser.parser.naUnit.Name.Name

/**
 * Parse num, sd, and unit from a  full quantifier string, falling back to last parsed value if supplied.
 * Used in {@link ContinuousValueExtractField} to set the parsed value
 */
export const parseQuantifierVal = (val, lastNum?, lastSD?, lastUnit?) => {
	const q = QuantifierParser.parse(val || '')
	if (q) {
		const u = q.High.Units[0]
		const num = u.Number != null ? u.num_str : ''
		const sd = u.SD != null ? u.sd_str : ''
		let unit = q?.High?.Units?.map(u => u.Unit.Name.Name)?.filter(n => !emptyUnit(n)).join('/')
		//if (emptyUnit(unit)) unit = ''
		return { num, sd, unit }
	} else {
		return { num: lastNum ?? '', sd: lastSD ?? '', unit: lastUnit ?? '' }
	}
}

/**
 * Variable class for quantifier-based fields.
 * Quantifier components are defined explicitly on construction due to complexity of the
 * recursive reactivity.
 */
export class VQuantifier extends Variable.with({
	val: VContinuousRowValue,
	advanced: VBoolean,
	//num: VContinuousNum,
	//sd: VSD,
	//unit: VString
}) {
	constructor(...args) {
		super(...args)
		let initialized = false
		const parsed = all([this.val, this.advanced]).to(e => {
			const [val, advanced] = e //e.value()
			if (advanced && initialized) return
			initialized = true
			const q = QuantifierParser.parse(val || '')
			if (q) {
				const u = q.High.Units[0]
				const num = u.Number != null ? u.num_str : ''
				const sd = u.SD != null ? u.sd_str : ''
				let unit = q?.High?.Units?.map(u => u.Unit.Name.Name)?.filter(n => !emptyUnit(n)).join('/')
				return { num, sd, unit }
			} else {
				return { num: '', sd: '', unit: '' }
			}
		}, ({num, sd, unit}, val) => {
			console.log(this.val)
			if (this.advanced.valueOf()) {
				sd = !!sd && Number.isFinite(Number(sd.trim())) ? sd : undefined
				const newVal = (num || '') + (sd == undefined ? '' :  (' ' + PLUS_MINUS + ' ' + sd)) + ((!!unit && !emptyUnit(unit)) ? (' ' + unit) : '')
				this.val.put(newVal)
			}
		})
		const _num = new VString()
		const _sd = new VString()
		const _unit = new VString()
		const setParsedField = (fieldVar, field) => {
			let initialized = false
			return ([parsed, advanced]) => {
				if (advanced) {
					if (!initialized) {
						fieldVar.put(parsed?.[field])
						initialized = true
					}
					return fieldVar
				} else {
					fieldVar.put(parsed?.[field])
					initialized = true
					return parsed?.[field]
				}
			}
		}
		const reverseParsedField = (fieldVar) =>
				val => {
					if (this.advanced.valueOf()) {
						fieldVar.put(val)
						parsed.put({
							num: fieldVar === _num ? val : _num.valueOf(),
							sd: fieldVar === _sd ? val : _sd.valueOf(),
							unit: fieldVar === _unit ? val : _unit.valueOf()
						})
					}
				}

		this.num = all([parsed, this.advanced]).to(setParsedField(_num, 'num'), reverseParsedField(_num)).as(VContinuousNum)
		this.sd = all([parsed, this.advanced]).to(setParsedField(_sd, 'sd'), reverseParsedField(_sd)).as(VSD)
		this.unit = all([parsed, this.advanced]).to(setParsedField(_unit, 'unit'), reverseParsedField(_unit)).as(VString)
	}
}

const validDosage = validationCtx('Dosage', validateFn(m => {
	const q = QuantifierParser.parse(m)
	return q && validateQuantifier(q) && classifyQuantifier(q).includes('dosage')
}))
/** Variable class dosages */
export const VDosage = VQuantifier.with({
	validate: m => validDosage(m.valueOf()?.val)
})

const validTimepoint = validateFn(m => !!TIMEPOINT.match(m))
/** Variable class for timepoints */
export const VTimepoint = VQuantifier.with({
	exists: VBoolean,
	a: Variable,
	validate: tp => {
		tp = tp.valueOf()
		return tp?.exists ? validResult() : validTimepoint(tp?.val)
	}
})

/** Generic (Row) name validator */
export const validName = validateFn(obj => obj && obj.val && obj.term /* ?.label, ?.senses?.length */)

const COMBO_SEPARATOR = /\w+\s*[+,/]\s*\w+/
const PARENS = /[()]/

/** Validator for Intervention names, implements additional intervention name restrictions */
export const validItvnName = (name: EName, existing?: EIntervention) => {
	const result: Validation = []
	result.isValid = false
	if (!name || !name.term) {
		return result
	}
	// XXX: EV-1369 hack to validate non-edited, negated, termid-less, label-less interventions
	if (!!existing) {
		if (name.val == existing.name.val && name.term.label == existing.name.val && _.isEqual(name.term.senses, existing.name.term.senses)) {
			result.isValid = true
			return result
		}

	}
	if (!!name.val) {
		result.isValid = true
		if (COMBO_SEPARATOR.test(name.val)) {
			result.isValid = false
			result.push('Only one treatment can be entered. Click Add Intervention to add second treatment to group.')
		}
		if (PARENS.test(name.val)) {
			result.isValid = false
			result.push('Parentheses cannot be used in the intervention field. Instead, a dash or colon can be used.')
		}
	}
	return result
}
/** Variable class for Intervention names */
export const VInterventionName = Variable.with({
	val: VString,
	a: Variable,
	term: Variable,
	validate: o => validItvnName(o.valueOf())
})

const validTreatment = validationCtx(null, validateFn(treatment => !(treatment?.measure?.val || '').trim() || validDosage(treatment.measure.val).isValid))
/** Variable class for Treatments */
export const VTreatment = Variable.with({
	measure: VDosage,
	validate: treatment => validTreatment(treatment.valueOf())
})

const validIntervention = validationCtx('Intervention', validateFn(itvn => itvn && validItvnName(itvn.name, itvn.existing).isValid && (!itvn.treatments?.length || (itvn.treatments.length > 0 && _.every(itvn.treatments, t => validTreatment(t).isValid))), 'Invalid intervention'))
/** Variable class for Interventions */
export class VIntervention extends Variable.with({
	id: VString,
	treatments: [VTreatment],
	name: VInterventionName,
	validate: itvn => validIntervention(itvn.valueOf())
}) {}

/** Used to prohibit settings cell N values for StudyLevel cells */
const prohibitN = validationCtx('Group N', validateFn(n => !str(n), 'N value cannot be specified', true))
const validN = n => (n = n.trim()).length && Number.isInteger(Number(n)) && Number(n) > 0
export const validStudyN = validationCtx('Study N', validateFn(n => !n || validN(n), 'Invalid N value', true))
const validGroupN = validationCtx('Group N', validateFn(validN, 'Invalid N value', 'N value must not be empty'))
interface ENValue {
	val: string
	a?: Annotation
}
/** Variable class for study-level N values - may be empty */
export const VStudyN = Variable.with({
	val: VString,
	a: Variable,
	validate: n => {
		n = n.valueOf()
		return validStudyN(n.val)
	}
})
/** Variable class for group N values - may not be empty */
export const VGroupN = Variable.with({
	val: VString,
	a: Variable,
	validate: n => {
		n = n.valueOf()
		return validGroupN(n?.val)
	}
})

export const isValidGroup = g =>
	/*validGroupN(g.n.val).isValid,*/
	(g.existing && _.isEmpty(diffExtractGroups(g, g.existing))) ||
	g.interventions && g.interventions.length && _.every(g.interventions, i => validIntervention(i).isValid)

const validGroup = validationCtx('Group', validateFn(isValidGroup))
class VExistingGroup extends Variable.with({
	id: VString,
	n: VGroupN,
	interventions: [VIntervention],
	chars: [Variable],
	validate: g => {
		g = g.valueOf()
		return validGroup(g)
	}
}) {}
/** Variable class for Group; contains an optional reference to an existing group */
export class VGroup extends VExistingGroup.with({
	existing: VExistingGroup
}) {}

const validSD = (s = '') => {
	if (typeof s == 'string') {
		s = s.trim()
		if (!s.length) return true
	}
	//let n = Number(s)
	// if (!Number.isInteger(n)) {
	//   const match = /^±(.*)$/.exec(s.trim())
	//   n = match && Number(match[1].trim())
	// }
	//return n > 0 //!Number.isNaN(n)
	return validPositiveNumber(s)
}

export const validSDValue = val => {
	const result = [] as Validation
	val = normalize(val)
	const isEmpty = empty(val)
	if (isEmpty) {
		result.isValid = true
		result.push('While variance is not required, unless variance is entered, this study cannot be included in the analysis.')
	} else {
		result.isValid = validPositiveNumber(val)
		if (!result.isValid) result.push('Must be non-negative number.')
	}
	return result
}
/** Variable class for Binary Cells */
export const VBinaryCell = Variable.with({
	val: VString
})

export const binaryCellValidation = (cell, unit) => all([cell, cell.val, unit], (cell, val, unit) =>
	!cell ? validResult() :
		((cell.existing && _.isEmpty(diffExtractCell(cell, cell.existing))) ||
		(!str(cell.n?.val) && !str(cell.val)) ? validResult() : (unit == '%' ? validBinaryPctValue(val) : validCountValue(val)))
)

export const cellNValidation = cell => cell.to(cell =>
	!cell ? validResult() :
		((cell.existing && _.isEmpty(diffExtractCell(cell, cell.existing))) ||
		(!str(cell.n?.val) && !str(cell.val)) ? validResult() : validGroupN(cell.n?.val))
)

/** Variable class for Count type Cells */
export const VCountCell = Variable.with({
	val: VString
})

export const countCellValidation = val => val.to(val=> validCountValue(val))

/** Variable class for a Continuous Cell */
export const VContinuousCell = Variable.with({
	val: VContinuousRowValue,
	num: VContinuousNum,
	sd: VSD,
	unit: VString,
	validate: o => validContinuousQuantifier(o.valueOf()?.val)
})

const FORMATTED_CI_RE = new RegExp(RE_FORMATTED_CI)
export const validCIValue = (s = '') => FORMATTED_CI_RE.test(s)
export const validContinuousQuantifier = validationCtx(null, validateFn(s => {
	if (!s) return false
	const match = CONT_VALUE.match(s)
	return !!match && _.compact(['Low', 'High'].map(field => match.parsed.quantifier[field])).every(bound => validSD(bound.Units[0].SD))
	//return !!match
}))

const findExactUnit = value => lookupUnit(value, indexedUnits())
export const validUnit = s => s && !!s.trim() && findExactUnit(s)
export const validOutcomeUnit = s => {
	if (s && !!s.trim()) {
		const quantifier = tryParseQuantifier('1 ' + s, true)
		return quantifier && validateQuantifier(quantifier, false, true, false)
	}
}

const validateTextCell = validationCtx('Text', validateFn((obj: any = {}) => notEmpty(str(obj.val))))
const validateBinaryPctCell = validationCtx('Binary Value', validateFn((obj: any = {}) => validBinaryPctValue(obj.val).isValid))
const validateBinaryCountCell = validationCtx('Binary Value', validateFn((obj: any = {}) => validBinaryCountValue(obj.val).isValid))
const validateCountCell = validationCtx('Count', validateFn((obj: any = {}) => validCountValue(obj.val).isValid))
export const validateContinuousCell = validationCtx('Continuous Value', validateFn((obj: any = {}) =>
	validContinuousQuantifier(obj.val).isValid, 'Invalid continuous value'))

/** maps row types to respective cell validations */
const ROW_VALIDATIONS = {
	BinaryCount: validateBinaryCountCell,
	BinaryPct: validateBinaryPctCell,
	Continuous: validateContinuousCell,
	Count: validateCountCell,
	Text: validateTextCell
}

const validCell = validationCtx(null, validateFn((cell, type: string, section: string, unit, allowEmpty = false) => {
	if (!cell) return allowEmpty
	if (cell.existing && _.isEmpty(diffExtractCell(cell, cell.existing))) return true
	if (!cell.existing && allowEmpty && !str(cell?.n?.val) && !str(cell?.val)) return true
	if (type == 'Binary') {
		type = 'Binary' + (unit == '%' ? 'Pct' : 'Count')
	}
	const validator = ROW_VALIDATIONS[type]
	if (!validator) throw new Error('Unknown outcome type: ' + type)
	return _.every([
		(section == 'StudyLevel' ? prohibitN : validGroupN)(cell?.n?.val).isValid,
		validator(cell).isValid
	])
}, null, true))

class VExistingCell extends Variable.with({
	val: VString,
	n: VGroupN,
	advanced: VBoolean,
	a: Variable,
}) {}
/** Variable class for Cells; contains optional reference to existing Cell */
class VCell extends VExistingCell.with({
	existing: VExistingCell
}) {}
/** Variable class for Row name */
const VRowName = Variable.with({
	val: VString,
	a: Variable,
	term: Variable,
	validate: o => validName(o.valueOf())
})

export const isValidRow = row => {
	let diff = row
	const validContinuousUnit = (row, unit) =>
		// require unit for Standard and Change Continuous types
		!(row.type == 'Continuous' && ['Standard', 'Change'].includes(row.rowType || 'Standard')) ||
			validOutcomeUnit((unit || '').trim())
	if (row.existing) {
		diff = diffExtractRows(row, row.existing)
		if (_.isEmpty(diff)) return true
		return (!diff.name || validName(diff.name).isValid) &&
			// require unit for Standard and Change Continuous types
			(!diff.unit || validContinuousUnit(row, diff.unit)) &&
			(!diff.timepoint || !diff.timepoint?.val || validTimepoint(diff.timepoint.val).isValid)
	} else {
		return validName(row.name).isValid &&
			// require unit for Standard and Change Continuous types
			validContinuousUnit(row, row.unit) &&
			(!row.timepoint?.val || validTimepoint(row.timepoint.val).isValid)
	}
}

const validRow = validationCtx(null, validateFn(isValidRow))

/** Validates both the row configuration and its associated cells */
export const isValidRowAndCells = (row: ERow, groups?, allowEmpty = false) =>
	isValidRow(row) &&
	((row.existing && !!row.cells && _.isEqual(row.cells, row.existing.cells)) || (
	!!row.cells && _.every(
		//.concat(Object.keys(row.cells))
		_.uniq((groups || []).map(g => g.id)).map(gid => row.cells[gid]).map(cell =>
			(cell?.existing && _.isEqual(cell, cell.existing)) ||
			validCell(cell, row.type, row.section, row.unit, allowEmpty).isValid
		)
	)
	))

export const validRowAndCells = validationCtx(null, validateFn(isValidRowAndCells))

class VExistingRow extends Variable.with({
	id: VString,
	name: VRowName,
	section: VString,
	/* field type */
	type: VString,
	rowType: VString,
	valueType: VString,
	devType: VString,
	initIsPositive: VBoolean,
	isPositive: VBoolean,
	timepoint: VTimepoint,
	unit: VString,
	// cells by group id
	cells: Variable,
	definition: VString,
	method: VString,
	validate: o => validRow(o.valueOf())
}) {}
/** Variable class for Row; contains optional reference to existing Row */
export class VRow extends VExistingRow.with({
	existing: VExistingRow,
	validate: r => validRow(r.valueOf())
}) {}

export const rowToStr = (row: ERow, dflt = 'Outcome') => (row.name?.term?.label || dflt) + (row.timepoint?.val ? ` @${row.timepoint.val}` : '')
/** VRow subclass that validates both row and cells */
export class VRowAndCells extends VRow.with( {
	validate: r => validRowAndCells(r.valueOf())
}) {}

/** Min, max, and initial groups for analysis type */
export const ANALYSIS_GROUPS = {
	auto: {
		min: 1,
		init: 2
	},
	cohort: {
		min: 1
	},
	direct: {
		min: 2
	},
	bayesian: {
		min: 2
	},
	frequentist: {
		min: 2
	}
}

export const analysisMinGroups = analysisType => analysisType ? (ANALYSIS_GROUPS[analysisType]?.min || 2) : 0
export const analysisMaxGroups = analysisType => ANALYSIS_GROUPS[analysisType]?.max ?? Infinity
const analysisInitGroups = analysisType => analysisType ? (ANALYSIS_GROUPS[analysisType]?.init || ANALYSIS_GROUPS[analysisType]?.min || 2) : 0
const hasMinGroups = validationCtx(null, validateFn((groups, analysisType) =>
	groups?.length >= analysisMinGroups(analysisType)
, 'Insufficient # of groups entered for this analysis type'))
const hasNumGroups = validationCtx(null, validateFn((groups, analysisType) =>
	groups?.length <= analysisMaxGroups(analysisType)
, 'Too many groups entered for this analysis type'))

/**
 * Variable for top-level Extraction viewmodel structure.
 */
export class VExtraction extends Variable.with({
	analysisType: VString,
	automated: VBoolean,
	importSource: VString,
	outcomes: [VRowAndCells],
	studyLevel: [VRowAndCells],
	groupLevel: [VRowAndCells],
	characteristics: [VRowAndCells],
	groups: [VGroup],
	note: VString,
	validate: validationCtx(null, validateFn(e => {
		const { analysisType, outcomes, studyLevel = [], groupLevel = [], characteristics = [], groups } = e.valueOf()
		const checks = [
			// XXX: newly-added rows have correct # of group/cells specified
			hasMinGroups(groups, analysisType).isValid,
			hasNumGroups(groups, analysisType).isValid,
			_.every(groups.map(g => validGroup(g).isValid)),
			_.every(outcomes.map(r => validRowAndCells(r, groups).isValid)),
			_.every(studyLevel.map(r => validRowAndCells(r, groups).isValid)),
			_.every(groupLevel.map(r => validRowAndCells(r, groups).isValid)),
			_.every(characteristics.map(r => validRowAndCells(r, groups).isValid))
		]
		return _.every(checks)
	}))
}) {}

/** Returns whether the study has outcomes or groups NOT originating from RE */
export function hasDocDataResults(study: DAStudy) {
	return _.some(study.outcomes, o => !o.extractor) || _.some(study.groups, g => !isRapidExtract(g))
}

/** Whether this study is eligible for cell extraction */
export function extractEligible(study: DAStudy) {
	return study.source != 'ClinicalTrials.gov' && study.source != 'EU Clinical Trials'
}

// we might pass a SerializedAlternatives from DS cooccurence terms, but those don't have directionality anyway
// so don't worry about trying to match them
const DAOutcomeIdToPhrase = (id: Sense | SerializedPhrase | SerializedPhraseWithTrailingType) => {
	const parts = (id as string).split(';')
	if (parts.length > 1) parts.pop()
	const senses = splitPhrase(parts[0] as SerializedPhrase)
	if (!senses[senses.length - 1]) senses.pop() // drop trailing empty sense if root phrase
	return senses
}

/* types for structures returned in DA search results */

/** Outcome returned by studies search, derived from topcooccurrences, these appear to be identified by individual sense */
export interface DAOutcome {
	id: Sense
	isPositive?: boolean
	children: (DARootChildOutcome | DAChildOutcome)[]
	label: string
	labels: string[]
	type?: string
}
/* Outcome derived from DocSearch, these appear to be identified by full serialized set of alternate phrases */
// interface DSOutcome {
// 	id: SerializedAlternatives
// 	label: string
// 	score: number
// }
interface DAChildOutcome extends Omit<DAOutcome, 'id' | 'children'> {
	id: SerializedPhrase
}
type SerializedPhraseWithTrailingType = Opaque<'SerializedPhraseWithTrailingType', string>
interface DARootChildOutcome extends Omit<DAOutcome, 'id'> {
	id: SerializedPhraseWithTrailingType
}
// cumulative outcomes is a combination of DA top cooccurrences and DS cooccurrences; only interested in DAOutcomes
//type CumulativeOutcomes = (DAOutcome | DSOutcome)[]

export interface DASearchRow {
	id: SerializedAlternatives
	label: string
	type: string
	unit: string
	isPositive?: boolean
}
export interface DASearchOutcome extends DASearchRow {
}
export interface DASearchCharacteristic extends DASearchRow {
}
export interface DASearchIntervention {
	id: SerializedAlternatives
	/* not actually sure if this is really a sequence of joined alternatives
	   or a sequence of individual alternatives (more likely), but split on ','
	   just in case */
	ids?: SerializedAlternatives[]
	label: string
	contains?: DASearchIntervention[]
	type?: 'intervention'
}

/**
 * Intermediate extraction initialization structure - derived either from data selected in study list,
 * or manually loaded from Extraction History
 */
export interface LoadedExtraction {
	id?: number
	analysisType?: string
	loaded: boolean
	outcomes: DAStudyRow[]
	studyLevel: DAStudyRow[]
	groupLevel: DAStudyRow[]
	characteristics: DAStudyRow[]
	groups: DAStudyGroup[]
}

const PLACEBO_GROUP: DAStudyGroup = {
	Interventions: [{
		termIds: [[PLACEBO_SENSE]],
		name: 'Placebo'
	}]
}

/** Try to look up the inferred directionality set on outcomes returned by DA */
export function findDirectionality(phrases: SerializedPhrase[] = [], outcomes: DAOutcome[] = []) {
	if (!phrases.length) return
	for (const outcome of outcomes) {
		// children can be reassigned a pending promise that doesn't resolve, just skip them
		const children = Array.isArray(outcome.children) ? outcome.children : []
		const matches = [outcome, ...children].some(o =>
			phrases.some(s => s == DAOutcomeIdToPhrase(outcome.id).join('/'))
		)
		if (matches) return outcome.isPositive
	}
}

/** For the given DAStudyRow, try to find and assign inferred directionality from the returned DAOutcomes */
export function getStudyRow(row: DAStudyRow, rows: DAOutcome[] = []): DAStudyRow {
	if (row.isPositive == undefined) {
		const isPositive = findDirectionality([
			row.matchingTerm as unknown as SerializedPhrase ?? row.termIds?.[0] ? joinPhrase(row.termIds[0]) : undefined
		], rows)
		if (isPositive != undefined) {
			row = {
				...row,
				isPositive
			}
		}
	}
	return row
}

/** Convert searched/selected outcome structures to DAStudyRows */
function getSearchOutcome(selectedOutcomes: DASearchOutcome[]): DAStudyRow {
	const o = (selectedOutcomes || []).filter(o => !!o.id)[0]
	return o && ({
		termIds: splitAlternatives(o.id).map(sp => splitPhrase(sp)),
		type: o.type == 'outcome' ? undefined : o.type,
		name: o.label,
		unit: o.unit,
		isPositive: o.isPositive
	})
}

const isPlacebo = (label, alternatives: Alternatives) =>
	/placebo(?:es|s)/i.test(label) || alternatives.some(phrase =>
		// if the entire phrase is placebo
		phrase.length == 1 && phrase[0] == PLACEBO_SENSE
	)

/**
 * Convert searched/selected intervention structures to DAStudyGroups
 * We create one group for each intervention, and make an effort to use the label of the first text occurrence
 * in title or abstract annotations.
 */
export function getSearchGroups(searchedTreatments: DASearchIntervention[], studyAnnotations: StudyAnnotations): DAStudyGroup[] {
	/* ungroup the treatments */
	searchedTreatments = searchedTreatments.flatMap(tmnt => tmnt.contains ?? [tmnt])
	/* filter out clear duplicates */
	/* group by entire set of ids/alternatives */
	const byName = _.groupBy(searchedTreatments, tmnt =>
		/* be very conservative: they are identical if the entire set of alternatives matches exactly */
		joinAlternatives(
			//@ts-ignore just join all alternatives into a single key
			tmnt.id ? [tmnt.id] : (tmnt.ids || [])
		)
	)
	const annotations = studyAnnotations.title.concat(studyAnnotations.abstract)
	searchedTreatments = _.transform(byName, (list, tmnts) => {
		if (tmnts.length == 1) {
			list.push(tmnts[0])
		} else {
			list.push(
				/* if multiple interventions with identical senses, pick the one whose text occurs first in the title or abstract */
				_.minBy(tmnts, tmnt => {
					const i = annotations.findIndex(a => a.types?.includes('intervention') && a.text.trim().toLowerCase().includes(tmnt.label?.trim().toLowerCase()))
					return i == -1 ? Infinity : i
				})
			)
		}
	}, [])
	searchedTreatments = _.uniqWith(searchedTreatments, (a, b) => {
		const aid = a.id ? [a.id] : (a.ids || [])
		const bid = b.id ? [b.id] : (b.ids || [])
		return aid.length && bid.length && _.isEqual(aid, bid)
	})
	return searchedTreatments.map(itvnTerm => ({
		// initialize a group for each searched intervention
		Interventions: [{
			termIds: splitAlternatives(itvnTerm.id || itvnTerm.ids?.[0]).map(splitPhrase),
			name: itvnTerm.label,
			OriginalName: itvnTerm.label
		}]
	}))
}

const termsRelated = (a: Sense[] = [], b: Sense[] = []) =>
	// just consider the primary terms
	!!a[0] && !!b[0] && a[0] == b[0]
/** tests if two different alternative sets are in some way related, heuristically */
function phrasesRelated(a: SerializedPhrase[], b: SerializedPhrase[], strict = false) {
	return _.some(a, pa => {
		if (strict) return _.some(b, pb => pa == pb)
		const senses = splitPhrase(pa)
		return _.some(b, pb => termsRelated(senses, splitPhrase(pb)))
	})
}

export const altsForSelectedRow = (outcome: DASearchOutcome) => splitAlternatives(outcome.id)

function altsForSelectedItvn(itvn: DASearchIntervention) {
	return [itvn].concat(itvn.contains || []).flatMap((itvn: DASearchIntervention) =>
		(itvn.id ? [itvn.id] : []).concat(itvn.ids || []).map(sAlts => splitAlternatives(sAlts))
	)
}

/**
 * Query is intersection of outcomes, interventions, and conditions/characteristics
 * Determine whether each of outcomes, interventions, characteristics are
 * 1) cited in abstract annotations or are already referenced in existing extracted study data
 * 2) are present in query
 * and
 * 3) determine if *all* query terms are satisfied by extraction
 */
export function detectTermsPresence(extractedOutcomes: ERow[], extractedGroups: EGroup[], fullStudy, dsAnnotations: Annotation[], queryAnnotations: QueryAnnotations, selectedOutcomes: DASearchOutcome[] = [], selectedInterventions: DASearchIntervention[] = []) {
	const selectedOutcomeAlts = selectedOutcomes.map(altsForSelectedRow)
	const outcomes = detectTermPresence(
		extractedOutcomes.filter(o => o?.name?.term).map(o => o.name.term),
		fullStudy?.outcomes,
		dsAnnotations,
		queryAnnotationsForType(queryAnnotations, 'outcome'),
		selectedOutcomeAlts
	)
	const selectedItvnAlts = selectedInterventions.map(altsForSelectedItvn).flat()
	const interventions = detectTermPresence(
		extractedGroups.flatMap(g => (g.interventions || []).map(i => i.name?.term)).filter(Boolean),
		(fullStudy?.groups).flatMap(g => g.Interventions).filter(Boolean),
		dsAnnotations,
		queryAnnotationsForType(queryAnnotations, 'intervention'),
		selectedItvnAlts
	)
	const conditionAnnotations = queryAnnotationsForType(queryAnnotations, 'condition')
	const conditions = {
		annotations: conditionAnnotations,
		matchesQuery:
			!!fullStudy?.Id /* if there is a returned study result, then presumably the searched condition will continue to match*/
			|| extractedMatchesQuery(conditionAnnotations, fullStudy?.conditions as Alternatives[], phrases => phrases.map(phrase => joinPhrase(phrase)))
	}
	return {
		outcomes,
		interventions,
		conditions,
		matchesQuery: outcomes.matchesQuery && interventions.matchesQuery && conditions.matchesQuery
	}
}

/** Returns the search (bar) query annotations of the given type */
export function queryAnnotationsForType(queryAnnotations: QueryAnnotations, termType) {
	return queryAnnotations.filter(a =>
		// annotations is an array of string or pico metadata entries
		!_.isString(a) && (a.ids || a.id) && a.type == termType
	).map((a: QueryAnnotation) => ({
		// sometimes there is no 'ids' array
		// pico metadata id is comma-delimited list of (/-delimited) terms
		...a,
		ids: a.ids || a.id.split(',')
	})) as QueryAnnotation[]
}

/** Returns whether the extracted entity name are all present in the search query */
function extractionInQuery<T>(annotations: QueryAnnotations, selectedEntities: SerializedPhrase[][] = [], records: T[] = [], toPhrases: (rec: T) => SerializedPhrase[]) {
	return (annotations.length + selectedEntities.length) == 0 ||
		// every record phrase matches a queried/selected phrase of corresponding type
		_.every(records, record => {
			const alts = toPhrases(record)
			// one of the terms related to the record is present in the query and/or selected entities
			return annotations.some(a => phrasesRelated(a.ids, alts)) ||
				selectedEntities.some(ealts => phrasesRelated(ealts, alts))
		})
}

/** Returns whether the search query terms are all present in the extracted entity names */
function queryInExtraction<T>(annotations: QueryAnnotations, selectedEntities: SerializedPhrase[][] = [], records: T[] = [], toPhrases: (rec: T) => SerializedPhrase[]) {
	// we want to see if the entered outcomes do not match known suggested alternative to any searched outcome
	// first, find the query metadata for the selectedOutcome
	// 	.filter(phrases =>
	// 	_.some(searched, o => phrases.includes(o.id))
	// )
	// then, for each extracted outcome, determine if it is missing from current search alternatives
	return (annotations.length + selectedEntities.length) == 0 ||
		annotations.every(a =>
			// one of the query phrases matches an extracted term
			_.some(records, record =>
				// one of the terms related to the record is present in the query
				phrasesRelated(a.ids, toPhrases(record))
			)
		) &&
		selectedEntities.every(ealts =>
			_.some(records, record =>
				phrasesRelated(ealts, toPhrases(record))
			)
		)
}

/** Determins which extracted names are matching and missing from the search query */
export function detectTermPresence(extractedRecordTerms: ETerm[], studyRecords: DAStudyRow[] | DAIntervention[] = [], dsAnnotations: Annotation[], queryAnnotations: QueryAnnotations, selectedEntities: SerializedAlternatives[]) {
	// records for which none of the related terms are present in the docsearch annotations
	const missingTerms = extractedRecordTerms.filter(term => !_.some(dsAnnotations, a => phraseRelated(term,  a.terms || [])))
	const matchesQuery = extractionInQuery(queryAnnotations, selectedEntities, extractedRecordTerms, term => term.senses)
	const missing = missingTerms.length > 0
	const inStudy = missingTerms.some(term =>
		studyRecords.some(studyRecord => phrasesRelated((studyRecord.termIds || []).map(joinPhrase), term.senses))
	)
	const result = {
		missing,
		inStudy,
		matchesQuery,
		warn: (missing && inStudy) || !matchesQuery,
		error: missing && !inStudy
	}
	return result
}

/** Load an extraction update contents from extraction history */
export function loadExtraction(analysisType: string, study: StudyContext, extraction): LoadedExtraction {
	const data = extraction.Data || {}
	/* produce a row comprised of DA formatted row overlayed with the DD extraction Row */
	const composeRow = (erow, studyRowsById) => {
		const srow = studyRowsById[erow.Id] || {}
		const r = {
			...srow,
			...erow,
			label: erow.Name || srow.label,
			cells: erow.NormalizedCells
		}
		// take directionality from extracted row
		if ('IsPositive' in erow) r.isPositive = erow.IsPositive
		return r
	}
	const outcomes = (data.Sections?.Outcomes?.Rows || []).map(erow => composeRow(erow, study.rowsById))
	const studyLevel = (data.Sections?.StudyLevel?.Rows || []).map(erow => composeRow(erow, study.rowsById))
	const groupLevel = (data.Sections?.GroupLevel?.Rows || []).map(erow => composeRow(erow, study.rowsById))
	const characteristics = (data.Sections?.Characteristics?.Rows || []).map(erow => composeRow(erow, study.rowsById))
	const rows = outcomes.concat(studyLevel, groupLevel, characteristics)
	/* groups which are referenced by the rows in this extraction */
	// TODO: eventually we plan to just render all groups in study with EV-501
	let groups = (study.groups || []).filter(g =>
		rows.some(o => _.some(o.cells, c => c.group == g.Id))
	)
	// if there are actually no cells referencing groups, try all the groups in this extraction
	if (groups.length == 0) {
		groups = study.groups.filter(g => (data.Groups || []).some(g => g.Id == g.Id))
	}
	return {
		id: extraction.Id,
		analysisType,
		loaded: true,
		outcomes,
		studyLevel,
		groupLevel,
		characteristics,
		groups
	}
}

/** Load search/selected results for extraction */
export function loadResults(analysisType: string, selectedOutcome: DAStudyOutcome, selectedGroups: DAStudyGroup[], searchedOutcomes, searchedGroupedItvns, coOutcomes: DAOutcome[], dsAnnotations: StudyAnnotations): LoadedExtraction {
	// take outcome and group from selected entities or searched entities if none selected */
	const outcome = selectedOutcome ? getStudyRow(selectedOutcome, coOutcomes) : getSearchOutcome(searchedOutcomes)
	const groupsSpecified = !!selectedGroups?.length
	let groups = groupsSpecified ? selectedGroups.filter(g => g.Interventions) : getSearchGroups(searchedGroupedItvns, dsAnnotations)
	// if we are initializing groups from searched interventions...
	if (!groupsSpecified) {
		// and have an abstract with annotations, limit interventions to those present in abstract or title
		const annotations = _.concat(dsAnnotations.title, dsAnnotations.abstract)
		if (annotations.length) {
			const abstractItvns = annotations.filter(a => a.types?.includes('intervention'))
			groups = _.transform(groups, (groups, g) => {
				g.Interventions = g.Interventions.filter(itvn => {
					const phrases = itvn.termIds.map(joinPhrase)
					return abstractItvns.some(a => phrasesRelated(phrases, a.senses as unknown as SerializedPhrase[]) || a.text.toLowerCase() == itvn.name?.toLowerCase())
				})
				if (g.Interventions.length) groups.push(g)
			})
		}
		const initGroups = analysisInitGroups(analysisType)
		// if the selected groups < min required groups for analysis, push *1* placebo group
		if (groups.length < initGroups) {
			if (!groups.some(g => g.Interventions?.some(itvn => isPlacebo(itvn.name, itvn.termIds)))) {
				groups.push(_.clone(PLACEBO_GROUP))
			} else {
				groups.push({
					Interventions: [{
						termIds: undefined,
						name: undefined
					}]
				})
			}
		}
	}
	return {
		analysisType,
		loaded: !!(selectedOutcome && selectedGroups?.length),
		outcomes: outcome ? [outcome] : [],
		studyLevel: [],
		groupLevel: [],
		characteristics: [],
		groups
	}
}

/** Terms referenced in the loaded extraction state */
export interface ReferencedTerms {
	interventions: SerializedPhrase[]
	outcomes: SerializedPhrase[]
	binaryOutcomes: SerializedPhrase[]
	ctnsOutcomes: SerializedPhrase[]
}

/** Return the terms referenced in the loaded extraction; this is used to inform the abstract annoation process */
export function getReferencedTerms(data: LoadedExtraction) {
	const { outcomes, groups } = data
	const binaryOutcomes = outcomes.filter(o => inferRowTypes(o, BINARY_STANDARD).fieldType == 'Binary')
	const ctnsOutcomes = outcomes.filter(o => inferRowTypes(o, BINARY_STANDARD).fieldType == 'Continuous')
	return {
		interventions: groups.flatMap(g => g.Interventions.flatMap(i => i.termIds?.map(joinPhrase))),
		outcomes: outcomes.flatMap(o => o.termIds?.map(joinPhrase)),
		binaryOutcomes: binaryOutcomes.flatMap(o => o.termIds?.map(joinPhrase)),
		ctnsOutcomes: ctnsOutcomes.flatMap(o => o.termIds?.map(joinPhrase))
	}
}

/** Create an EGroup, optionally given an existing reference */
export function createExtractGroup(group?: EGroup): EGroup {
	return group ? {
		..._.cloneDeep(group),
		existing: group
	} : {
		id: groupId(),
		n: { val: '' },
		interventions: [createExtractItvn()]
	}
}

/** Initialize a new Extraction structure */
export function newExtraction(initial: LoadedExtraction) {
	const extraction: Extraction = {
		analysisType: initial.analysisType,
		groups: [],
		outcomes: [],
		studyLevel: [],
		groupLevel: [],
		characteristics: [],
		//chars: [{ name: DEMO_LTS.age.name }],
		note: ''
	}
	const { groups } = initial
	for (const group of groups) {
		const loaded = toExtractGroup(group)
		extraction.groups.push(group.Id ? createExtractGroup(loaded) : loaded)
	}

	const groupsById = _.keyBy(extraction.groups, g => g.id);
	['studyLevel', 'groupLevel', 'characteristics', 'outcomes'].forEach(section => {
		const sctn = _.upperFirst(section)
		const rows: DAStudyRow[] = initial[section]
		for (const row of rows || []) {
			const loaded = toExtractRow(sctn, row, groupsById)
			if (!loaded.rowConfig) continue
			let extractRow
			if (row.id) {
				extractRow = createExtractRow(sctn, loaded)
			} else {
				extractRow = loaded
			}
			extraction[section].push(extractRow)
		}
	})
	return extraction
}

/** count new and existing entities in comparison to study */
function summarizeEditability(comparisons: ExtractionComparison['comparisons']) {
	const cells = [new Set(), new Set()]
	const groups = [new Set(), new Set()]
	const outcomes = [new Set(), new Set()]
	const update = (extracted, matching, type) => matching?.length > 0 ? matching.forEach(item => type[1].add(item)) :
	                                              extracted.forEach(item => type[0].add(item))
	for (const { group, matchingGroups, row, matchingRows, matchingCells } of comparisons) {
		update([group], matchingGroups, groups)
		update([row], matchingRows, outcomes)
		update(matchingCells ? [] : [{}], matchingCells, cells)
	}
	return [cells[0].size, cells[1].size, groups[0].size, groups[1].size, outcomes[0].size, outcomes[1].size] as const
}

/** generate a message from list of new and existing entities */
const comparisonMessage = ([newCells, existingCells, newGroups, existingGroups, newOutcomes, existingOutcomes]) => {
	let msg = ''
	const conjoin = (list) => {
		if (list.length > 1) list[list.length - 1] = 'and ' + list[list.length - 1]
		return list.length > 2 ? list.join(', ') : list.join(' ')
	}
	const desc = (type, counts) => ['new', 'existing'].map((state, i) =>
		counts[i] ? `${counts[i]} ${state} ${pluralize(type, counts[i])}` : ''
	)
	msg += conjoin(_.compact(desc('datapoint', [newCells, existingCells])))
	msg += ' for '
	msg += conjoin(_.compact([
		...desc('group', [newGroups, existingGroups]),
		...desc('outcome', [newOutcomes, existingOutcomes])
	]))
	return msg
}

/** create a comma separated list phrase */
const csl = (list, oxford = true) => {
	const [initial, last] = [_.initial(list), _.last(list)]
	return initial.length ? [initial.join(', '), last].join(initial.length > 1 && oxford ? ', and ' : ' and ') : last
}

/** generate a message and list of errors from the comparison state */
export function comparisonState(comparisons: ExtractionComparison['comparisons']) {
	const [editable, uneditable] = _.partition(comparisons, ({uneditable}) => !uneditable)
	const errors = _.map(_.groupBy(uneditable, c => c.matchingRows?.[0]?.name), byRows => {
		let s = 'Cannot overwrite existing data'
		const entities = []
		if (byRows[0].matchingRows?.length) entities.push(`row '${byRows[0].matchingRows?.[0]?.name}'`)
		const groups = byRows.filter(r => r.matchingGroups?.length).map(r => r.matchingGroups?.[0].OriginalName)
		if (groups.length) entities.push(pluralize('group', groups.length) + ' ' + csl(groups.map(g => `'${g}'`)))
		if (entities.length) s += ' for '
		s += entities.join(' and ')
		return s
	})
	return {
		message: editable.length ? 'Extracting ' + comparisonMessage(summarizeEditability(editable)) : undefined,
		errors
	}
}

/** return query conditions which are not present in the extraction */
export function deriveConditions(queryAnnotations: QueryAnnotations, conditionAlts: SerializedPhrase[][] = [], fullStudy) {
	const annotations = queryAnnotationsForType(queryAnnotations, 'condition')
	const matchesQuery =
			!!fullStudy?.Id /* if there is a returned study result, then presumably the searched condition will continue to match*/
			|| queryInExtraction(annotations, conditionAlts, fullStudy?.conditions as Alternatives[], phrases => phrases.map(phrase => joinPhrase(phrase)))
	if (!matchesQuery && annotations.length) {
		return annotations.map(a => a.label)
	}
}

export const submitExtractions = (data: ExtractionRequest, onSubmit?) =>
	post(`/portal/extract`, { data }).then(newStudy => onSubmit?.())
